// Cost Accounting Data Structure
// Systematic naming convention for future Supabase migration:
// - Tables: cost_centers, cost_elements, cost_allocations, production_costs, standard_costs
// - Primary Keys: cost_center_id, cost_element_id, allocation_id, production_cost_id, standard_cost_id
// - Foreign Keys: production_order_id, work_order_id, product_id, account_id
// - Related Tables: production_orders, work_orders, inventory_items, chart_of_accounts
// - Indexes: cost_period, cost_type, status, product_code

export const costCenterTypes = [
  {
    type_id: 'PRODUCTION',
    type_name: '生產中心',
    type_name_en: 'Production Center',
    description: '直接參與生產活動的成本中心',
    color_code: '#3B82F6', // Blue
    icon: 'Factory',
    is_active: true
  },
  {
    type_id: 'SERVICE',
    type_name: '服務中心',
    type_name_en: 'Service Center',
    description: '提供服務支援的成本中心',
    color_code: '#8B5CF6', // Purple
    icon: 'Tool',
    is_active: true
  },
  {
    type_id: 'ADMINISTRATIVE',
    type_name: '行政中心',
    type_name_en: 'Administrative Center',
    description: '管理和行政相關的成本中心',
    color_code: '#10B981', // Green
    icon: 'Briefcase',
    is_active: true
  }
];

export const costElementTypes = [
  {
    type_id: 'DIRECT_MATERIAL',
    type_name: '直接材料',
    type_name_en: 'Direct Material',
    description: '可直接歸屬於產品的材料成本',
    color_code: '#3B82F6', // Blue
    icon: 'Package',
    is_active: true,
    allocation_base: 'MATERIAL_QUANTITY'
  },
  {
    type_id: 'DIRECT_LABOR',
    type_name: '直接人工',
    type_name_en: 'Direct Labor',
    description: '可直接歸屬於產品的人工成本',
    color_code: '#F59E0B', // Amber
    icon: 'Users',
    is_active: true,
    allocation_base: 'LABOR_HOURS'
  },
  {
    type_id: 'MANUFACTURING_OVERHEAD',
    type_name: '製造費用',
    type_name_en: 'Manufacturing Overhead',
    description: '無法直接歸屬於產品的間接生產成本',
    color_code: '#8B5CF6', // Purple
    icon: 'Settings',
    is_active: true,
    allocation_base: 'MACHINE_HOURS'
  },
  {
    type_id: 'NON_MANUFACTURING',
    type_name: '非製造成本',
    type_name_en: 'Non-manufacturing Cost',
    description: '與生產無直接關係的成本',
    color_code: '#10B981', // Green
    icon: 'DollarSign',
    is_active: true,
    allocation_base: 'DIRECT_COST_RATIO'
  }
];

export const costCenters = [
  {
    cost_center_id: 'CC_001',
    cost_center_code: 'CC-001',
    cost_center_name: '裁剪部門',
    cost_center_name_en: 'Cutting Department',
    cost_center_type: 'PRODUCTION',
    description: '負責布料裁剪的生產部門',
    manager: '王主管',
    location: '工廠A區',
    is_active: true,
    budget_amount: 120000.00,
    actual_amount: 115500.00,
    variance_amount: 4500.00,
    variance_percentage: 3.75,
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_center_id: 'CC_002',
    cost_center_code: 'CC-002',
    cost_center_name: '縫紉部門',
    cost_center_name_en: 'Sewing Department',
    cost_center_type: 'PRODUCTION',
    description: '負責布料縫製的生產部門',
    manager: '李主管',
    location: '工廠B區',
    is_active: true,
    budget_amount: 180000.00,
    actual_amount: 185000.00,
    variance_amount: -5000.00,
    variance_percentage: -2.78,
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_center_id: 'CC_003',
    cost_center_code: 'CC-003',
    cost_center_name: '洗滌部門',
    cost_center_name_en: 'Washing Department',
    cost_center_type: 'PRODUCTION',
    description: '負責牛仔褲洗滌處理的生產部門',
    manager: '張主管',
    location: '工廠C區',
    is_active: true,
    budget_amount: 150000.00,
    actual_amount: 148000.00,
    variance_amount: 2000.00,
    variance_percentage: 1.33,
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_center_id: 'CC_004',
    cost_center_code: 'CC-004',
    cost_center_name: '品檢部門',
    cost_center_name_en: 'Quality Control Department',
    cost_center_type: 'PRODUCTION',
    description: '負責成品品質檢驗的部門',
    manager: '陳主管',
    location: '工廠D區',
    is_active: true,
    budget_amount: 90000.00,
    actual_amount: 92000.00,
    variance_amount: -2000.00,
    variance_percentage: -2.22,
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_center_id: 'CC_005',
    cost_center_code: 'CC-005',
    cost_center_name: '設備維護部門',
    cost_center_name_en: 'Equipment Maintenance',
    cost_center_type: 'SERVICE',
    description: '負責生產設備維護和修理的部門',
    manager: '林主管',
    location: '工廠E區',
    is_active: true,
    budget_amount: 70000.00,
    actual_amount: 68500.00,
    variance_amount: 1500.00,
    variance_percentage: 2.14,
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_center_id: 'CC_006',
    cost_center_code: 'CC-006',
    cost_center_name: '行政管理部門',
    cost_center_name_en: 'Administrative Department',
    cost_center_type: 'ADMINISTRATIVE',
    description: '負責工廠行政管理的部門',
    manager: '黃經理',
    location: '辦公大樓2樓',
    is_active: true,
    budget_amount: 120000.00,
    actual_amount: 118000.00,
    variance_amount: 2000.00,
    variance_percentage: 1.67,
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  }
];

export const costElements = [
  {
    cost_element_id: 'CE_001',
    cost_element_code: 'CE-001',
    cost_element_name: '牛仔布料',
    cost_element_name_en: 'Denim Fabric',
    cost_element_type: 'DIRECT_MATERIAL',
    account_id: 'ACC_5110', // Raw Materials
    unit_of_measure: '米',
    unit_cost: 120.00,
    is_active: true,
    description: '製造牛仔褲的主要材料',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_element_id: 'CE_002',
    cost_element_code: 'CE-002',
    cost_element_name: '縫線',
    cost_element_name_en: 'Thread',
    cost_element_type: 'DIRECT_MATERIAL',
    account_id: 'ACC_5110', // Raw Materials
    unit_of_measure: '卷',
    unit_cost: 25.00,
    is_active: true,
    description: '牛仔褲縫製用線',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_element_id: 'CE_003',
    cost_element_code: 'CE-003',
    cost_element_name: '鈕扣和拉鍊',
    cost_element_name_en: 'Buttons and Zippers',
    cost_element_type: 'DIRECT_MATERIAL',
    account_id: 'ACC_5110', // Raw Materials
    unit_of_measure: '組',
    unit_cost: 15.00,
    is_active: true,
    description: '牛仔褲的鈕扣和拉鍊配件',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_element_id: 'CE_004',
    cost_element_code: 'CE-004',
    cost_element_name: '裁剪工資',
    cost_element_name_en: 'Cutting Labor',
    cost_element_type: 'DIRECT_LABOR',
    account_id: 'ACC_5210', // Direct Labor
    unit_of_measure: '小時',
    unit_cost: 200.00,
    is_active: true,
    description: '布料裁剪工人的工資',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_element_id: 'CE_005',
    cost_element_code: 'CE-005',
    cost_element_name: '縫紉工資',
    cost_element_name_en: 'Sewing Labor',
    cost_element_type: 'DIRECT_LABOR',
    account_id: 'ACC_5210', // Direct Labor
    unit_of_measure: '小時',
    unit_cost: 220.00,
    is_active: true,
    description: '縫紉工人的工資',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_element_id: 'CE_006',
    cost_element_code: 'CE-006',
    cost_element_name: '洗滌工資',
    cost_element_name_en: 'Washing Labor',
    cost_element_type: 'DIRECT_LABOR',
    account_id: 'ACC_5210', // Direct Labor
    unit_of_measure: '小時',
    unit_cost: 180.00,
    is_active: true,
    description: '牛仔褲洗滌工人的工資',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_element_id: 'CE_007',
    cost_element_code: 'CE-007',
    cost_element_name: '機器折舊',
    cost_element_name_en: 'Machine Depreciation',
    cost_element_type: 'MANUFACTURING_OVERHEAD',
    account_id: 'ACC_5310', // Manufacturing Overhead
    unit_of_measure: '月',
    unit_cost: 50000.00,
    is_active: true,
    description: '生產設備的折舊費用',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_element_id: 'CE_008',
    cost_element_code: 'CE-008',
    cost_element_name: '水電費',
    cost_element_name_en: 'Utilities',
    cost_element_type: 'MANUFACTURING_OVERHEAD',
    account_id: 'ACC_5310', // Manufacturing Overhead
    unit_of_measure: '月',
    unit_cost: 35000.00,
    is_active: true,
    description: '生產過程中的水電費用',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_element_id: 'CE_009',
    cost_element_code: 'CE-009',
    cost_element_name: '間接人工',
    cost_element_name_en: 'Indirect Labor',
    cost_element_type: 'MANUFACTURING_OVERHEAD',
    account_id: 'ACC_5310', // Manufacturing Overhead
    unit_of_measure: '月',
    unit_cost: 85000.00,
    is_active: true,
    description: '生產主管和間接工人的工資',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  },
  {
    cost_element_id: 'CE_010',
    cost_element_code: 'CE-010',
    cost_element_name: '行政費用',
    cost_element_name_en: 'Administrative Expenses',
    cost_element_type: 'NON_MANUFACTURING',
    account_id: 'ACC_5600', // Administrative Expenses
    unit_of_measure: '月',
    unit_cost: 120000.00,
    is_active: true,
    description: '公司行政管理費用',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-15T00:00:00Z'
  }
];

export const productionCosts = [
  {
    production_cost_id: 'PC_2025_001',
    production_order_id: 'PO_2025_001',
    product_code: 'J001',
    product_name: '經典直筒牛仔褲',
    cost_period: '2025-01',
    quantity_produced: 500,
    status: 'FINALIZED',
    direct_material_cost: 45000.00,
    direct_labor_cost: 30000.00,
    manufacturing_overhead: 25000.00,
    total_production_cost: 100000.00,
    unit_cost: 200.00,
    standard_cost: 190.00,
    cost_variance: 10.00,
    variance_percentage: 5.26,
    created_by: 'user_zhang',
    created_at: '2025-01-25T00:00:00Z',
    updated_at: '2025-01-25T00:00:00Z',
    notes: '正常生產，無特殊情況'
  },
  {
    production_cost_id: 'PC_2025_002',
    production_order_id: 'PO_2025_002',
    product_code: 'J002',
    product_name: '修身窄管牛仔褲',
    cost_period: '2025-01',
    quantity_produced: 300,
    status: 'FINALIZED',
    direct_material_cost: 30000.00,
    direct_labor_cost: 22000.00,
    manufacturing_overhead: 18000.00,
    total_production_cost: 70000.00,
    unit_cost: 233.33,
    standard_cost: 220.00,
    cost_variance: 13.33,
    variance_percentage: 6.06,
    created_by: 'user_zhang',
    created_at: '2025-01-25T00:00:00Z',
    updated_at: '2025-01-25T00:00:00Z',
    notes: '部分材料成本上漲'
  },
  {
    production_cost_id: 'PC_2025_003',
    production_order_id: 'PO_2025_003',
    product_code: 'J003',
    product_name: '寬鬆男友風牛仔褲',
    cost_period: '2025-01',
    quantity_produced: 400,
    status: 'FINALIZED',
    direct_material_cost: 48000.00,
    direct_labor_cost: 24000.00,
    manufacturing_overhead: 20000.00,
    total_production_cost: 92000.00,
    unit_cost: 230.00,
    standard_cost: 240.00,
    cost_variance: -10.00,
    variance_percentage: -4.17,
    created_by: 'user_zhang',
    created_at: '2025-01-25T00:00:00Z',
    updated_at: '2025-01-25T00:00:00Z',
    notes: '生產效率提高，成本低於標準'
  },
  {
    production_cost_id: 'PC_2025_004',
    production_order_id: 'PO_2025_004',
    product_code: 'J004',
    product_name: '高腰直筒牛仔褲',
    cost_period: '2025-01',
    quantity_produced: 350,
    status: 'DRAFT',
    direct_material_cost: 38500.00,
    direct_labor_cost: 24500.00,
    manufacturing_overhead: 21000.00,
    total_production_cost: 84000.00,
    unit_cost: 240.00,
    standard_cost: 230.00,
    cost_variance: 10.00,
    variance_percentage: 4.35,
    created_by: 'user_zhang',
    created_at: '2025-01-25T00:00:00Z',
    updated_at: '2025-01-25T00:00:00Z',
    notes: '成本計算尚未最終確認'
  }
];

export const costDetails = [
  // PC_2025_001 - 經典直筒牛仔褲
  {
    detail_id: 'PCD_2025_001_001',
    production_cost_id: 'PC_2025_001',
    cost_element_id: 'CE_001',
    cost_element_name: '牛仔布料',
    cost_center_id: 'CC_001',
    cost_center_name: '裁剪部門',
    quantity: 750,
    unit_of_measure: '米',
    unit_cost: 120.00,
    total_cost: 30000.00,
    notes: '標準用量：每件1.5米'
  },
  {
    detail_id: 'PCD_2025_001_002',
    production_cost_id: 'PC_2025_001',
    cost_element_id: 'CE_002',
    cost_element_name: '縫線',
    cost_center_id: 'CC_002',
    cost_center_name: '縫紉部門',
    quantity: 100,
    unit_of_measure: '卷',
    unit_cost: 25.00,
    total_cost: 2500.00,
    notes: '標準用量：每20件1卷'
  },
  {
    detail_id: 'PCD_2025_001_003',
    production_cost_id: 'PC_2025_001',
    cost_element_id: 'CE_003',
    cost_element_name: '鈕扣和拉鍊',
    cost_center_id: 'CC_002',
    cost_center_name: '縫紉部門',
    quantity: 500,
    unit_of_measure: '組',
    unit_cost: 15.00,
    total_cost: 7500.00,
    notes: '每件一組配件'
  },
  {
    detail_id: 'PCD_2025_001_004',
    production_cost_id: 'PC_2025_001',
    cost_element_id: 'CE_004',
    cost_element_name: '裁剪工資',
    cost_center_id: 'CC_001',
    cost_center_name: '裁剪部門',
    quantity: 50,
    unit_of_measure: '小時',
    unit_cost: 200.00,
    total_cost: 10000.00,
    notes: '每10件1小時'
  },
  {
    detail_id: 'PCD_2025_001_005',
    production_cost_id: 'PC_2025_001',
    cost_element_id: 'CE_005',
    cost_element_name: '縫紉工資',
    cost_center_id: 'CC_002',
    cost_center_name: '縫紉部門',
    quantity: 100,
    unit_of_measure: '小時',
    unit_cost: 220.00,
    total_cost: 22000.00,
    notes: '每5件1小時'
  },
  {
    detail_id: 'PCD_2025_001_006',
    production_cost_id: 'PC_2025_001',
    cost_element_id: 'CE_006',
    cost_element_name: '洗滌工資',
    cost_center_id: 'CC_003',
    cost_center_name: '洗滌部門',
    quantity: 50,
    unit_of_measure: '小時',
    unit_cost: 180.00,
    total_cost: 9000.00,
    notes: '每10件1小時'
  },
  {
    detail_id: 'PCD_2025_001_007',
    production_cost_id: 'PC_2025_001',
    cost_element_id: 'CE_007',
    cost_element_name: '機器折舊',
    cost_center_id: 'CC_001',
    cost_center_name: '裁剪部門',
    quantity: 0.2,
    unit_of_measure: '月',
    unit_cost: 50000.00,
    total_cost: 10000.00,
    notes: '按生產數量分攤'
  },
  {
    detail_id: 'PCD_2025_001_008',
    production_cost_id: 'PC_2025_001',
    cost_element_id: 'CE_008',
    cost_element_name: '水電費',
    cost_center_id: 'CC_003',
    cost_center_name: '洗滌部門',
    quantity: 0.3,
    unit_of_measure: '月',
    unit_cost: 35000.00,
    total_cost: 10500.00,
    notes: '洗滌部門水電用量較高'
  },
  {
    detail_id: 'PCD_2025_001_009',
    production_cost_id: 'PC_2025_001',
    cost_element_id: 'CE_009',
    cost_element_name: '間接人工',
    cost_center_id: 'CC_006',
    cost_center_name: '行政管理部門',
    quantity: 0.2,
    unit_of_measure: '月',
    unit_cost: 85000.00,
    total_cost: 17000.00,
    notes: '生產主管和品管人員工資'
  }
];

export const standardCosts = [
  {
    standard_cost_id: 'SC_J001',
    product_code: 'J001',
    product_name: '經典直筒牛仔褲',
    effective_from: '2025-01-01',
    effective_to: '2025-12-31',
    direct_material_standard: 85.00,
    direct_labor_standard: 55.00,
    manufacturing_overhead_standard: 50.00,
    total_standard_cost: 190.00,
    created_by: 'user_zhang',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-01T00:00:00Z',
    notes: '基於上一季度實際成本設定',
    is_active: true
  },
  {
    standard_cost_id: 'SC_J002',
    product_code: 'J002',
    product_name: '修身窄管牛仔褲',
    effective_from: '2025-01-01',
    effective_to: '2025-12-31',
    direct_material_standard: 95.00,
    direct_labor_standard: 65.00,
    manufacturing_overhead_standard: 60.00,
    total_standard_cost: 220.00,
    created_by: 'user_zhang',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-01T00:00:00Z',
    notes: '基於上一季度實際成本設定',
    is_active: true
  },
  {
    standard_cost_id: 'SC_J003',
    product_code: 'J003',
    product_name: '寬鬆男友風牛仔褲',
    effective_from: '2025-01-01',
    effective_to: '2025-12-31',
    direct_material_standard: 110.00,
    direct_labor_standard: 70.00,
    manufacturing_overhead_standard: 60.00,
    total_standard_cost: 240.00,
    created_by: 'user_zhang',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-01T00:00:00Z',
    notes: '基於上一季度實際成本設定',
    is_active: true
  },
  {
    standard_cost_id: 'SC_J004',
    product_code: 'J004',
    product_name: '高腰直筒牛仔褲',
    effective_from: '2025-01-01',
    effective_to: '2025-12-31',
    direct_material_standard: 100.00,
    direct_labor_standard: 70.00,
    manufacturing_overhead_standard: 60.00,
    total_standard_cost: 230.00,
    created_by: 'user_zhang',
    created_at: '2025-01-01T00:00:00Z',
    updated_at: '2025-01-01T00:00:00Z',
    notes: '基於上一季度實際成本設定',
    is_active: true
  }
];
